import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon, SentIcon, Tick02Icon } from '@hugeicons/core-free-icons';
import { toUserFacingMessage } from '@overlayqa/shared';
import { useModalA11y } from '@/hooks/useModalA11y';
import { ModalPortal } from '@/components/shared/ModalPortal';
import { capture } from '@/lib/analytics';
import { log } from '@/lib/logger';
import { useSetClientReviewRecipients } from '../../hooks/useClientReviews';
import { ClientEmailsField, cleanClientEmails } from './ClientEmailsField';

interface ClientEmailsModalProps {
  reviewId: string;
  /** Emails already on the link: empty opens "Add client emails", otherwise "Edit client emails". */
  initialEmails: string[];
  /** Addresses whose invite failed; the hourly tick retries them (up to 3 tries). */
  failedEmails?: string[];
  onClose: () => void;
}

/**
 * Add or change a link's client emails (docs/prototypes/client-feedback-reminders.html).
 * A new email gets the link once the page is ready and its own reminders from that email; removing one
 * stops its reminders and the link keeps working.
 */
export function ClientEmailsModal({ reviewId, initialEmails, failedEmails = [], onClose }: ClientEmailsModalProps) {
  const isEdit = initialEmails.length > 0;
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, onClose);
  const setRecipients = useSetClientReviewRecipients();
  const [rows, setRows] = useState<string[]>(isEdit ? initialEmails : ['']);
  const [invalidIndex, setInvalidIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = cleanClientEmails(rows);
    if (!cleaned.ok) { setInvalidIndex(cleaned.invalidIndex); return; }
    if (!isEdit && cleaned.emails.length === 0) { setInvalidIndex(0); return; }
    capture(isEdit ? 'client_emails_save_clicked' : 'client_emails_send_clicked', { email_count: cleaned.emails.length });
    try {
      const result = await setRecipients.mutateAsync({ id: reviewId, emails: cleaned.emails });
      const failed = result.invites.filter(i => !i.sent && !i.skipped && !i.pending).map(i => i.email);
      const pending = result.invites.filter(i => !i.sent && i.pending).map(i => i.email);
      const sent = result.invites.filter(i => i.sent).map(i => i.email);
      const skipped = result.invites.filter(i => !i.sent && i.skipped).map(i => i.email);
      if (sent.length) toast.success(`Sent to ${sent.join(', ')}.`);
      if (pending.length) toast.info(`Invitation queued for ${pending.join(', ')}. You don't need to send another invitation.`);
      if (failed.length) toast.error(`The email to ${failed.join(', ')} didn't send. Copy the link and share it yourself.`);
      if (skipped.length) toast.info(`Email was not sent to ${skipped.join(', ')}. Copy the link and share it yourself.`);
      onClose();
    } catch (err) {
      log.warn('[ClientEmailsModal] Save refused', { err });
      setError(toUserFacingMessage(err, 'Could not save the client emails. Try again.'));
    }
  };

  const title = isEdit ? 'Edit client emails' : 'Add client emails';
  return (
    <ModalPortal>
      <div
        ref={dialogRef}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-emails-modal-title"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden">
          <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
            <h2 id="client-emails-modal-title" className="oq-dialog__title">{title}</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} noValidate className="px-6 pt-4 pb-5">
            {!isEdit && <p className="modal-intro mb-4">Email this review link to your client and remind them if they haven't commented.</p>}
            <ClientEmailsField
              label="Client emails"
              hint={isEdit
                ? 'A new email gets the link once the page is ready, with reminders from that email. Removing an email stops its reminders; the link keeps working.'
                : "We'll email them the link once the page is ready, then remind them 3, 7 and 10 days after this email if they haven't commented."}
              rows={rows}
              onChange={(next) => { setRows(next); setInvalidIndex(null); setError(null); }}
              invalidIndex={invalidIndex}
              disabled={setRecipients.isPending}
              surface={isEdit ? 'edit' : 'add'}
            />
            {failedEmails.map(e => (
              <p key={e} className="mb-2 text-xs text-red-600">{e}: the email didn't send.</p>
            ))}
            {error && <p className="mb-4 text-sm text-red-600" role="alert">{error}</p>}
            <div className="flex gap-3 justify-end">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={setRecipients.isPending}>
                <HugeiconsIcon icon={Cancel01Icon} size={16} color="currentColor" className="w-4 h-4 mr-2" aria-hidden="true" />
                <span className="text-cap-trim">Cancel</span>
              </button>
              <button type="submit" className="btn-primary" disabled={setRecipients.isPending}>
                <HugeiconsIcon icon={isEdit ? Tick02Icon : SentIcon} size={16} color="currentColor" className="w-4 h-4 mr-2" aria-hidden="true" />
                <span className="text-cap-trim">{isEdit ? 'Save' : 'Send'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
